import React from 'react';
import { InvestorFormData } from '@/utils/formValidation/investorForm';
import { DatePicker } from '../ui/DatePicker';
import { PasswordField } from '../ui/PasswordField';
import { PhoneNumberField } from '../ui/PhoneNumberField';
import { StateDropdown } from '../ui/StateDropdown';
import { TextField } from '../ui/TextField';

type IndividualInvestorFormProps = {
  formData: InvestorFormData;
  onFieldChange: (field: keyof InvestorFormData, value: string | boolean) => void;
  getFieldError: (field: keyof InvestorFormData) => string | undefined;
};

export const IndividualInvestorForm: React.FC<IndividualInvestorFormProps> = ({
  formData,
  onFieldChange,
  getFieldError,
}) => {
  return (
    <div className="flex flex-col sm:flex-row w-full items-start justify-between gap-4 sm:gap-2">
      {/* Left Column */}
      <div className="flex h-full w-full sm:w-1/2 flex-col items-center justify-start gap-2 py-2 px-1">
        <div className="flex w-full flex-col items-start justify-start gap-2">
          {/* First Name */}
          <TextField
            value={formData.firstName}
            onChange={(value) => onFieldChange('firstName', value)}
            placeholder="First Name"
            error={!!getFieldError('firstName')}
          />
          {/* Middle Name */}
          <TextField
            value={formData.middleName}
            onChange={(value) => onFieldChange('middleName', value)}
            placeholder="Middle Name"
            error={!!getFieldError('middleName')}
          />
          {/* Lastname */}
          <TextField
            value={formData.lastname}
            onChange={(value) => onFieldChange('lastname', value)}
            placeholder="Last Name"
            error={!!getFieldError('lastname')}
          />

          {/* Date of Birth */}
          <DatePicker
            value={formData.dateOfBirth}
            onChange={(date) => onFieldChange('dateOfBirth', date)}
            placeholder="Date of Birth"
            label="Date of Birth"
            error={!!getFieldError('dateOfBirth')}
          />

          {/* Email */}
          <TextField
            value={formData.email}
            onChange={(value) => onFieldChange('email', value)}
            placeholder="Email"
            type="email"
            error={!!getFieldError('email')}
          />

          {/* Phone Number */}
          <PhoneNumberField
            value={formData.phone}
            onChange={(value) => onFieldChange('phone', value)}
            placeholder="Phone Number"
            error={!!getFieldError('phone')}
          />
        </div>
      </div>

      {/* Right Column */}
      <div className="flex h-full w-full sm:w-1/2 flex-col items-center justify-start gap-2 py-2 px-1">
        <div className="flex w-full flex-col items-start justify-start gap-2 self-stretch">
          {/* Address Line 1 */}
          <TextField
            value={formData.address1}
            onChange={(value) => onFieldChange('address1', value)}
            placeholder="Address Line 1"
            error={!!getFieldError('address1')}
          />

          {/* Address Line 2 */}
          <TextField
            value={formData.address2}
            onChange={(value) => onFieldChange('address2', value)}
            placeholder="Address Line 2"
            error={!!getFieldError('address2')}
          />

          {/* City and State */}
          <div className="flex w-full gap-2 relative z-10">
            <TextField
              value={formData.city}
              onChange={(value) => onFieldChange('city', value)}
              placeholder="City"
              error={!!getFieldError('city')}
            />
            <div className="relative flex-shrink-0 w-[120px]">
              <StateDropdown
                value={formData.state}
                onChange={(value) => onFieldChange('state', value)}
                placeholder="State"
                error={!!getFieldError('state')}
              />
            </div>
          </div>

          {/* Zip and Country */}
          <div className="flex w-full gap-2">
            <TextField
              value={formData.zip}
              onChange={(value) => onFieldChange('zip', value)}
              placeholder="Zip Code"
              error={!!getFieldError('zip')}
            />
            <TextField value="United States" onChange={() => {}} placeholder="Country" size="small" disabled />
          </div>

          {/* Password */}
          <PasswordField
            value={formData.password}
            onChange={(value) => onFieldChange('password', value)}
            placeholder="Password"
            error={!!getFieldError('password')}
          />

          {/* Confirm Password */}
          <PasswordField
            value={formData.repeat}
            onChange={(value) => onFieldChange('repeat', value)}
            placeholder="Confirm Password"
            error={!!getFieldError('repeat')}
          />
        </div>
      </div>
    </div>
  );
};
